import React, { useContext, useEffect, useRef, useState } from 'react'
import { getFunctions, httpsCallable } from "firebase/functions";

import Button from '@/components/Button';
import { AuthContext } from '@/contexts/AuthContext';
import { StakeContext } from '@/contexts/StakeContext';
import { ModalContext } from '@/contexts/ModalContext';
import { useQuery } from '@/hooks/useQuery';
import { convertAmount } from '@/utils/currencyResolver';
import { ExclamationCircleIcon, ExclamationIcon, UserIcon } from '@heroicons/react/solid'
import modalRoutes from '@/modalRoutes'
import { useSelector } from 'react-redux';
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { Navigate, useNavigate } from 'react-router-dom'
import { PlayerCard } from '../../components/Duel'
import { classNames } from '@/utils/classNames'
import errorToast from '@/utils/errorToast'
import { useBalance } from '@/hooks/useBalance'
import { toast } from 'react-toastify';
import Toast from '../../components/Toast';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

export default function StartDuel() {
  const [duelDetected, setDuelDetected] = useState(false)
  const [stake, setStake] = useState();
  const [stakeLoaded, setStakeLoaded] = useState(false);
  const [shouldGenerateRematch, setShouldGenerateRematch] = useState(true)
  const [isGeneratingRematch, setIsGeneratingRematch] = useState(false)
  const { auth } = useContext(AuthContext)
  const { closeModal } = useContext(ModalContext)
  const { translatedStakes } = useContext(StakeContext)
  const router = useNavigate()
  const query = useQuery()
  const id = query.get('id')
  const functions = getFunctions();
  const firestore = useFirestore()
  // const updateRematch = httpsCallable(functions, 'updateRematch');
  // const handleRematch = httpsCallable(functions, 'handleRematch');
  // const completeDuel = httpsCallable(functions, 'completeDuel');


  useEffect(() => {
    setStake(translatedStakes.find(s => s?.id === id))
    setStakeLoaded(true)
  }, [translatedStakes])

  /*
  //CAN BE USED LATER FOR LIVE HISTORY
  useEffect(async () => {
    const duelRef = await firestore.collection("duels").doc(stake?.id);
    const ticksRef = firestore.collection('ticks');
    const allTicksRes = await ticksRef.where('duel', '==', duelRef).onSnapshot(querySnapshot => {
      querySnapshot.docChanges().forEach(change => {
        if (change.type === 'added') {
          let _ticks = ticks;
          const _exists = _ticks && _ticks.find(t => t.id === change.doc.id)
          !_exists && _ticks.push({
            id: change.doc.id, 
            created: change.doc.data().created,
            joined: change.doc.data().joined,
            timestamp: change.doc.data().timestamp.toDate()
          })
          setTicks(_ticks)
        }
      });
    });
    return () => {
      allTicksRes();
    }
  }, [stake])
  */

  useEffect(() => {
    //Listening for changes to this active stake
    if (stake && stake?.duelStart) {
      setDuelDetected(true)
    }
  }, [stake])

  if (!stake && stakeLoaded) {
    closeModal()
  }

  useEffect(() => {
    if (stake?.id && auth.uid == stake?.createdUid) {
      firestore.collection("duels").doc(stake?.id).onSnapshot(snapshot => {
        if (snapshot?.data()?.declined) {
          closeModal()
        }
      })
    }
  }, [stake])

  if (stake?.joined !== auth.uid && stake?.created !== auth.uid) {
    return (
      <div className="py-12">
        <div className="flex justify-center">
          <ExclamationCircleIcon className="h-16 w-16" />
        </div>
        <p className="mt-3 mb-6 text-center">
          You do not have access to this duel. Please submit a challenge or create your own duel.
        </p>
        <div className="flex justify-center">
          <Button modifier="secondary" rounded="rounded-full" link to={modalRoutes.CREATE_DUEL} className="px-4 py-2.5">Create Duel</Button>
        </div>
      </div>
    )
  }

  if (stake?.completed) {
    return (
      <Navigate to="/arena" />
    )
  }

  return (
    <div className="flex flex-row rounded-2xl h-[600px] overflow-hidden">
      <div className="flex-1 flex flex-col">
        {stake && (duelDetected ? <DuelDetectedScreen stake={stake} /> : <DuelInfoScreen loaded={stakeLoaded} stake={stake} />)}
      </div>
      <div className="flex-1 bg-gray-900 rounded-2xl md:rounded-none absolute md:relative inset-0 transform translate-x-full md:translate-x-0">
        {stake && stake?.joined && <PrivateChat stake={stake} />}
      </div>
    </div>
  )
}

const PrivateChat = ({ stake }) => {
  const messageRef = useRef(null)
  const { userRef, auth, userBalance } = useContext(AuthContext)
  const [newMessage, setNewMessage] = useState('')
  const [messages, setMessages] = useState([])
  const query = useQuery()
  const id = query.get('id')
  const firestore = useFirestore();
  const functions = getFunctions();

  useFirestoreConnect({
    collection: `duels/${id}/chat`,
    orderBy: [
      ["timestamp"]
    ],
    storeAs: "duelMessages",
  });

  let _messages = useSelector((state) => state.firestore.data.duelMessages);
  useEffect(() => {
    _messages ? setMessages(Object.values(_messages).map(m => {
      return {
        user_id: m.from.id,
        message: m.message
      }
    })) : setMessages([])
  }, [_messages])

  const handleInput = e => {
    setNewMessage(e.target.value)
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (newMessage?.length == 0) return
    firestore.collection(`duels/${id}/chat`).add({
      message: newMessage,
      timestamp: firestore.Timestamp.fromDate(new Date()),
      from: firestore.collection("users").doc(auth?.uid)
    })
    setNewMessage('')
  }

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.scrollTop = messageRef.current.scrollHeight;
    }
  }, [messages])

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col h-[600px] overflow-hidden">
      <div className="grid gap-2 pb-2 relative overflow-auto p-6" ref={messageRef}>
        {messages?.map((message, index) => (
          <div key={index} className={`flex ${message.user_id == auth.uid ? 'justify-end' : 'justify-start'}`}>
            <span className="bg-gray-800 text-gray-300 py-2.5 px-4 rounded max-w-[65%] break-all">{message.message}</span>
          </div>
        ))}
      </div>
      <div className="mt-auto p-6">
        <div className="flex w-full p-1 bg-gray-800 text-white font-medium text-sm rounded-full focus:outline-none focus:shadow-none focus:ring-0 transition duration-300">
          <input value={newMessage} onChange={handleInput} className="px-4 py-2.5 bg-transparent placeholder-gray-300 border-none p-0" type="text" placeholder="Type your message" />
          <Button rounded="rounded-full" modifier="secondary" type="submit" className="px-6 py-2">Send</Button>
        </div>
      </div>
    </form>
  )
}

const CountdownTimer = ({ countdownFinishMs }) => {
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    const timer =
      counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  useEffect(() => {
    setCounter(Math.floor((new Date(countdownFinishMs) - new Date()) / 1000))
  }, [countdownFinishMs])

  const secondsToMinutes = seconds => Math.floor(seconds / 60) + ':' + ('0' + Math.floor(seconds % 60)).slice(-2);

  return (
    counter > 0 ? <p className="">{secondsToMinutes(counter)}</p> : <p>expired</p>
  )
}

const DuelAction = ({ stake }) => {
  const functions = getFunctions();
  const declineDuel = httpsCallable(functions, 'declineDuel');
  const cancelDuel = httpsCallable(functions, 'cancelDuel');
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { auth } = useContext(AuthContext)
  const { closeModal } = useContext(ModalContext)

  const handleDecline = async () => {
    try {
      setIsSubmitting(true)
      await declineDuel(stake?.id)
      closeModal()
    } catch (error) {
      errorToast(error)
    }
    setIsSubmitting(false)
  }
  const handleCancel = async () => {
    try {
      setIsSubmitting(true)
      await cancelDuel(stake?.id)
      closeModal()
    } catch (error) {

    }
    setIsSubmitting(false)
  }

  if (stake?.created == auth.uid) {
    return (
      <Button onClick={handleCancel} className="py-2.5 mt-auto w-full" modifier="danger">
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            Processing
            <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
        ) : (
          <span>Cancel Duel</span>
        )}
      </Button>
    )
  }

  return (
    <Button onClick={handleDecline} className="py-2.5 mt-auto w-full" modifier="danger">
      {isSubmitting ? (
        <span className="flex items-center justify-center">
          Processing
          <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      ) : (
        <span>Decline Duel</span>
      )}
    </Button>
  )
}

const DuelInfoScreen = ({ stake }) => {
  const { auth } = useContext(AuthContext)
  const { getUsernamesByDuelId } = useContext(StakeContext)
  const { closeModal } = useContext(ModalContext)
  const userIsHost = stake?.created == auth.uid
  const [duelAccount, setDuelAccount] = useState("")

  useEffect(() => {
    const setUsernames = async () => {
      const { created, joined } = await getUsernamesByDuelId(stake?.id)
      if (created && joined) {
        userIsHost ? setDuelAccount(joined) : setDuelAccount(created)
      }
    }
    if (stake?.joined) {
      setUsernames()
    }
  }, [stake, userIsHost])

  return (
    <>
      <div className="grid gap-4 flex-1 overflow-auto p-6">
        <p className="text-white text-center text-lg font-medium">Duel In Progress</p>
        <div>
          <label className="block mb-1 font-medium">Proceed to</label>
          <p className="bg-gray-900 text-xl font-bold shadow-md h-[52px] rounded px-4 py-2.5 text-gray-300 text-center border-2 border-gray-700">World 2, Duel Arena</p>
        </div>
        <div>
          <label className="block mb-1 font-medium">You must challenge</label>
          <p className="bg-gray-900 text-xl font-bold shadow-md h-[52px] rounded px-4 py-2.5 text-gray-300 text-center border-2 border-gray-700"><span className="max-w-[20ch] block truncate mx-auto">{duelAccount}</span></p>
        </div>
        <div>
          <label className="block mb-1 font-medium">This duel will expire in</label>
          <div className="bg-gray-900 text-xl font-bold shadow-md h-[52px] rounded px-4 py-2.5 text-gray-300 text-center border-2 border-gray-700">
            <CountdownTimer countdownFinishMs={stake?.expiresAt} />
          </div>
        </div>
        <p className="text-xs text-gray-200">Duels.gg is not responsible for any user errors. Please ensure you are challenging the correct user <span className="underline">every</span> time.</p>
        <p className="text-xs text-gray-200">Once you are detected inside of the arena, you will no longer be able to decline this duel.</p>
      </div>
      <div className="px-6 pb-6 w-full">
        <DuelAction stake={stake} />
      </div>
    </>
  )
}

const DuelDetectedScreen = ({ stake }) => {
  const functions = getFunctions()
  // const handleRematch = httpsCallable(functions, 'handleRematch');
  const { auth, userBalance } = useContext(AuthContext)
  const userIsHost = stake?.created == auth.uid
  const { offerRematch, getUsernamesByDuelId } = useContext(StakeContext)
  const currentUser = { uid: auth?.uid, balance: userBalance }
  const [showOfferRematch, setShowOfferRematch] = useState(false)
  const [rematchAmount, setRematchAmount] = useState(0)
  const [duelState, setDuelState] = useState("COUNTDOWN");
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [createdAccount, setCreatedAccount] = useState("")
  const [joinedAccount, setJoinedAccount] = useState("")

  const [joinedDoc, setJoinedDoc] = useState(null)
  const [createdDoc, setCreatedDoc] = useState(null)

  useEffect(() => {
    const setUsernames = async () => {
      const { created, joined } = await getUsernamesByDuelId(stake?.id)
      setJoinedAccount(joined)
      setCreatedAccount(created)
    }
    setUsernames()
  }, [stake, userIsHost])

  const loadPlayers = async () => {
    // const joinedDocData = await stake.joinedUser.get()
    if (stake?.created && !createdDoc) {
      const createdDocData = await stake.createdUser.get()
      setCreatedDoc(createdDocData)
    }
    if (stake?.joined && !joinedDoc) {
      const joinedDocData = await stake.joinedUser.get()
      setJoinedDoc(joinedDocData)
    }
  }

  useEffect(() => {
    loadPlayers()
  }, [stake])

  useEffect(() => {
    if (stake?.duelStart && !stake?.fightStart) {
      setDuelState("COUNTDOWN")
    } else if (stake?.duelStart && stake?.fightStart && !stake?.winner) {
      setDuelState("IN PROGRESS")
    } else if (stake?.winner) {
      setDuelState("COMPLETED")
    }
  }, [stake])

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col p-6 overflow-auto">
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="flex flex-col self-baseline">
            <label className="block mb-2 font-medium">{userIsHost ? `${createdAccount} (You)` : `${joinedAccount} (You)`}</label>
            {createdDoc && joinedDoc ? <PlayerCard stake={stake} player={userIsHost ? createdDoc : joinedDoc} type={userIsHost ? 'created' : 'joined'} /> : <SkeletonTheme baseColor="#141524" highlightColor="#191d2c" ><Skeleton height={176} /></SkeletonTheme>}
            {/* <PlayerCard stake={stake} player={userIsHost ? createdDoc : joinedDoc} type={userIsHost ? 'created' : 'joined'} /> */}
          </div>
          <div className="flex flex-col self-baseline">
            <label className="block mb-2 font-medium">{userIsHost ? `${joinedAccount} (Opponent)` : `${createdAccount} (Opponent)`}</label>
            {joinedDoc && joinedDoc ? <PlayerCard stake={stake} player={userIsHost ? joinedDoc : createdDoc} type={userIsHost ? 'joined' : 'created'} /> : <SkeletonTheme baseColor="#141524" highlightColor="#191d2c" ><Skeleton height={176} /></SkeletonTheme>}
            {/* <PlayerCard stake={stake} player={userIsHost ? joinedDoc : createdDoc} type={userIsHost ? 'joined' : 'created'} /> */}
          </div>
        </div>

        <div className="flex flex-1 justify-center flex-col items-center">
          {stake?.winner && (
            <div className="flex flex-col items-center mt-8">
              <p className="leading-none">Winner</p>
              <p className="text-2xl font-bold text-white">{stake?.winner == stake?.createdUid ? createdAccount : joinedAccount}</p>
            </div>
          )}
        </div>
      </div>
      {stake?.fightEnd && (
        <div className="p-6">
          <div className="grid">
            <Button link to="/arena" className="py-2.5 h-12" modifier="secondary">Back to lobby</Button>
          </div>
        </div>
      )}
    </div>
  )
}

export const HealthBar = ({ damage }) => {
  const HitpointToPercentage = 100 / 99
  const percentage = (99 - damage) * HitpointToPercentage

  return (
    <div className="relative flex h-5 w-full">
      <div className="bg-[#ff0100] inset-0 relative w-full"></div>
      <div className={`absolute left-0 inset-y-0 bg-[#00fe00] z-10 transition-all duration-300`} style={{ width: (damage < 99 ? percentage : 0) + "%" }}></div>
      <span className="absolute text-xs font-medium tracking-wide text-gray-900 inset-0 flex items-center justify-center z-10">{99 - damage}/99</span>
    </div>
  )
}